import React from 'react';
import { useTranslation } from 'react-i18next';
import { getCountry, getPayroll } from '../../../config/countries';
import type { PayrollTierId } from '../../../config/countries';
import { TIER_META } from '../constants';

export interface TierCardProps {
  tierId: PayrollTierId;
  employeeCount: number;
  planId: string;
  billing: string;
  onSelect: (id: PayrollTierId) => void;
  isSelected: boolean;
  onCardClick: (id: PayrollTierId) => void;
}

export function TierCard({ tierId, employeeCount, planId, billing, onSelect, isSelected, onCardClick }: TierCardProps): React.ReactElement {
  const { t } = useTranslation();
  const tier = getPayroll(tierId);
  const country = getCountry();
  const meta = TIER_META[tierId];
  const payrollOnly = tier.promo + (tier.perEmployee * employeeCount);

  const borderClass = isSelected
    ? "border-[3px] border-ink"
    : (meta.featured ? "border-2 border-ink" : "border border-hairline");
  const shadowStyle = isSelected
    ? { boxShadow: "0 20px 60px rgba(14,26,26,0.25)" }
    : (meta.featured ? { boxShadow: "0 20px 60px rgba(14,26,26,0.12)" } : {});
  const topMargin = (isSelected || meta.featured) ? 0 : 26;

  return (
    <div
      onClick={() => onCardClick(tierId)}
      className={"relative cursor-pointer rounded-2xl bg-white font-sans " + borderClass}
      style={{ padding: "28px 28px 28px 0", marginTop: topMargin, transition: "border 0.2s, box-shadow 0.2s", ...shadowStyle }}
    >
      <div
        className="absolute rounded font-extrabold text-white"
        style={{ top: -12, left: 24, background: meta.ribbon.bg, padding: "6px 14px", fontSize: 10, letterSpacing: "0.1em" }}
      >{t("addPayroll.ribbons." + meta.ribbon.key)}</div>

      {isSelected && (
        <div className="absolute z-[2] flex h-8 w-8 items-center justify-center rounded-full bg-ink" style={{ top: 16, right: 16 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3}><path d="M20 6L9 17l-5-5"/></svg>
        </div>
      )}

      <div style={{ marginTop: 4 }}>
        <div
          className="inline-block font-bold"
          style={{ background: meta.nameBadgeBg, color: meta.nameBadgeColor, padding: "5px 12px", fontSize: 12, borderTopRightRadius: 6, borderBottomRightRadius: 6 }}
        >{t("addPayroll.tiers." + tierId + ".name")}</div>
      </div>
      <div style={{ marginTop: 6 }}>
        <div
          className="inline-block font-medium"
          style={{ background: "#FAFAFA", color: "#1F2937", padding: "6px 12px", fontSize: 12, borderTopRightRadius: 6, borderBottomRightRadius: 6, borderLeft: "3px solid #046A38" }}
        >{t("addPayroll.tiers." + tierId + ".tagline")}</div>
      </div>

      <div style={{ padding: "0 0 0 28px" }}>
        <div style={{ marginTop: 16 }}>
          <div className="text-small text-helper line-through">${tier.regular}/mo</div>
          <div style={{ margin: "2px 0" }}>
            <span className="font-extrabold text-ink" style={{ fontSize: 36, letterSpacing: "-0.02em" }}>${tier.promo}</span>
            <span className="ml-1.5 text-small text-helper">/mo base</span>
          </div>
          <div className="text-small font-bold text-brand">{tierId === "core" ? t("addPayroll.saveCore") : tierId === "premium" ? t("addPayroll.savePremium") : t("addPayroll.saveElite")}</div>
          <div className="mt-0.5 text-tiny text-helper">{t("addPayroll.perEmployee", { amount: "$" + tier.perEmployee })}</div>
        </div>

        <div className="mt-3 rounded-md bg-brand-bg text-center" style={{ padding: 12 }}>
          <div className="text-tiny text-helper">{t(employeeCount === 1 ? "addPayroll.totalFor" : "addPayroll.totalFor_plural", { count: employeeCount })}</div>
          <div className="font-extrabold text-brand" style={{ fontSize: 20 }}>
            ${payrollOnly}<span className="text-tiny text-helper">/mo {country.currency}</span>
          </div>
        </div>

        <a
          onClick={(e) => { e.stopPropagation(); onSelect(tierId); }}
          className="mt-4 block cursor-pointer rounded-[10px] text-center font-extrabold text-body no-underline"
          style={{ padding: 14, background: "#53E875", color: "#00100E" }}
        >{t("addPayroll.addToPlan")}</a>

        <div style={{ marginTop: 20 }}>
          <div className="text-small font-bold text-ink">{t("addPayroll.tiers." + tierId + ".includesLabel")}</div>
          <div className="mt-2 h-0.5 rounded" style={{ background: "linear-gradient(90deg,#046A38 0%,#2E5FCE 100%)" }} />
        </div>
        <div className="mt-3 text-small text-sub" style={{ lineHeight: 1.9 }}>
          {(((t("addPayroll.tiers." + tierId + ".payroll", { returnObjects: true }) as string[]) || []).map((f, i) => <div key={"p" + i}>{"• " + f}</div>))}
          {(((t("addPayroll.tiers." + tierId + ".hours", { returnObjects: true }) as string[]) || []).map((f, i) => <div key={"h" + i}>{"• " + f}</div>))}
        </div>
      </div>
    </div>
  );
}
